import { Permission } from "@quest/slice/permissions/permission.slice";

const CRUD_SCOPES = ["create", "view", "edit", "delete"] as const;

function parseResourceScopeFromPermissionName(
  name: string
): { resource: string; scope: string } | null {
  for (const scope of CRUD_SCOPES) {
    const suffix = `-${scope}`;
    if (name.endsWith(suffix)) {
      return { resource: name.slice(0, -suffix.length), scope };
    }
  }
  return null;
}

function isScopeDenied(
  denials: Record<string, string[]>,
  resource: string,
  scope: string
): boolean {
  const list = denials[resource];
  return Array.isArray(list) && list.includes(scope);
}

/**
 * Effective role permissions for the current user: drop scopes that are denied
 * per resource (mentor-permissions / denials API).
 */
export function stripPermissionsByDenials(
  permissions: Permission[] | undefined,
  denials: Record<string, string[]> | undefined
): Permission[] {
  if (!permissions?.length) return [];
  if (!denials || typeof denials !== "object") return permissions;

  const out: Permission[] = [];

  for (const p of permissions) {
    if (p.resources?.length) {
      for (const resource of p.resources) {
        const scopes = (p.scopes ?? []).filter(
          (s) => !isScopeDenied(denials, resource, s)
        );
        if (scopes.length > 0) {
          out.push({ ...p, resources: [resource], scopes });
        }
      }
      continue;
    }

    const parsed = parseResourceScopeFromPermissionName(p.name);
    if (parsed && isScopeDenied(denials, parsed.resource, parsed.scope)) {
      continue;
    }

    out.push(p);
  }

  return out;
}
